<template>
  <div class="grid-x" :class="_containerCss">
    <div v-if="toggleDetails" class="child-summary-toggle-details">
      <cat-btn
        :id="id + '_toggleDetails'"
        rounded
        hollow
        small
        :aria-expanded="isExpanded.toString()"
        :aria-label="toggleDetailsLabel + ' ' + summaryLabel"
        :hint="toggleDetailsLabel"
        :aria-controls="toggleControlledId"
        class="toggle-details-btn mrt"
        @ui-click="handleToggleDetails($event)"
        ><cat-icon
          decorative
          class="size-l mn"
          :icon="isExpanded ? 'angle-up' : 'angle-down'"
        ></cat-icon
      ></cat-btn>
    </div>
    <div :class="contentClass" style="min-height: 1px">
      <slot></slot>
    </div>

    <div
      v-if="buttonCount < 1 && incompleteMarker"
      :class="childSummaryActionClass"
      role="group"
      :aria-label="summaryLabel"
    >
      <cat-repeater-child-marker
        :id="id"
        class="cell incomplete-marker"
        :type="incompleteMarker.type"
        :marker-text="
          incompleteMarker.message
            ? incompleteMarker.message
            : resolveTxt('skin.error.incomplete', 'incomplete')
        "
      ></cat-repeater-child-marker>
    </div>

    <div
      v-if="buttonCount > 0 && hasVisibleButtons"
      :class="childSummaryActionClass"
      role="group"
      :aria-label="summaryLabel"
    >
      <cat-repeater-child-marker
        v-if="incompleteMarker"
        :id="id"
        class="cell"
        :type="incompleteMarker.type"
        :marker-text="
          incompleteMarker.message
            ? incompleteMarker.message
            : resolveTxt('skin.error.incomplete', 'incomplete')
        "
      ></cat-repeater-child-marker>
      <slot name="buttons">
        <cat-btn
          v-if="allowEdit && !selected"
          class="edit-item mlt pn"
          icon="pencil"
          small
          button-tag
          link
          simple
          :label="editLabel"
          @ui-click="handleEdit"
          >{{ editLabel }}</cat-btn
        >
        <cat-btn
          v-if="allowView && !isExpanded"
          class="view-item mlt pn"
          button-tag
          small
          link
          simple
          :label="viewLabel"
          @ui-click="handleToggleDetails($event)"
          >{{ viewLabel }}</cat-btn
        >

        <cat-btn
          v-if="allowView && isExpanded"
          :class="['hide-item', 'mlt', 'pn']"
          button-tag
          small
          link
          simple
          :label="hideLabel"
          @ui-click="handleToggleDetails($event)"
          >{{ hideLabel }}
        </cat-btn>

        <cat-btn
          v-if="allowDelete && !selected"
          :class="deleteClasses"
          :icon="selected ? null : 'trash'"
          button-tag
          small
          :link="!selected"
          :secondary="selected"
          :label="deleteLabel"
          @ui-click="handleDelete"
          >{{ deleteLabel }}</cat-btn
        >
      </slot>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, inject } from "vue";
import { defaultResolveTxt } from "@/components/field-element";
import CatBtn from "@/components/catBtn/catBtn.vue";
import CatIcon from "@/components/utilities/catIcon.vue";
import CatRepeaterChildMarker from "@/components/catRepeaterChild/catRepeaterChildMarker";
import { hasSlotContent } from "@/components/utilities/slot-content.js";

export default defineComponent({
  setup() {
    // Inject the global value
    const resolveTxt = inject("resolveTxt", defaultResolveTxt);

    return {
      resolveTxt
    };
  },
  name: "CatRepeaterChildSummary",
  components: { CatRepeaterChildMarker, CatBtn, CatIcon },
  inject: {
    resolveTxt: { name: "resolveTxt", default: (key, def) => def }
  },
  props: {
    id: {
      type: [String, Number],
      description: "Repeater child detail ID."
    },
    selected: {
      type: Boolean,
      description: "Zero-based index of the currently selected record."
    },
    editable: {
      type: Boolean,
      description: "Not in use."
    },
    allowEdit: {
      type: Boolean,
      default: true,
      description: "Renders 'Edith' button to allow editing entry."
    },
    allowDelete: {
      type: Boolean,
      default: true,
      description: "Renders 'Delete' button."
    },
    allowView: {
      type: Boolean,
      default: false,
      description: "Renders the 'Show more' button to toggle details."
    },
    allowDone: {
      type: Boolean,
      default: false,
      description: "Not in use."
    },
    summaryLabel: {
      type: String,
      description: "Text label to uniquely identify this child summary"
    },
    summaryClasses: {
      type: Array,
      description:
        "Array of classes set into the child summary container markup."
    },
    editButtonLabel: {
      type: String,
      description: "Specifies 'Edith' button label."
    },
    deleteButtonLabel: {
      type: String,
      description: "Specifies 'Delete' button label."
    },
    doneButtonLabel: {
      type: String,
      description: "Specifies 'Done' button label."
    },
    viewButtonLabel: {
      type: String,
      description: "Specifies 'View' button label."
    },
    hideButtonLabel: {
      type: String,
      description: "Hides button label."
    },
    incompleteMarker: {
      type: Object,
      default: null,
      description:
        "Contains the fields: message - the incomplete text, childIndex - to identify the child offscreen, type - styling class (i.e. errors, warning, info, debug)"
    },
    toggleDetails: {
      type: Boolean,
      default: true,
      description: "Toggles entry details on toggle button click."
    },
    isExpanded: {
      type: Boolean,
      default: false,
      description: "Specifies if details are expanded."
    },
    toggleDetailsText: {
      type: String,
      description: "Specifies tool tip when hovering over toggle button."
    },
    toggleControlledId: {
      type: String,
      description: "Specifies child ID with '_details' as appendix."
    }
  },

  computed: {
    childSummaryActionClass: function () {
      return "child-summary-actions small-auto grid-x flex-align-right";
    },
    buttonCount: function () {
      if (hasSlotContent(this.$slots.buttons)) {
        return 1;
      }
      return (
        (this.allowDelete ? 1 : 0) +
        (this.allowView ? 1 : 0) +
        (this.allowEdit ? 1 : 0)
      );
    },
    contentClass: function () {
      return {
        "child-summary-body": true,
        "has-actions": this.buttonCount > 0
      };
    },
    toggleDetailsLabel: function () {
      return (
        this.toggleDetailsText ||
        this.resolveTxt("repeater.record.toggle.details", "More Details")
      );
    },
    deleteLabel: function () {
      return (
        this.deleteButtonLabel || this.resolveTxt("repeater.delete", "Delete")
      );
    },
    editLabel: function () {
      return this.editButtonLabel || this.resolveTxt("repeater.edit", "Edit");
    },
    viewLabel: function () {
      return (
        this.viewButtonLabel ||
        this.resolveTxt("repeater.show.more", "Show more")
      );
    },
    hideLabel: function () {
      return (
        this.hideButtonLabel ||
        this.resolveTxt("repeater.show.less", "Show less")
      );
    },
    doneLabel: function () {
      return this.doneButtonLabel || this.resolveTxt("repeater.done", "Done");
    },
    doneClasses: function () {
      return this.allowView && !this.allowEdit ? "eye-off" : "ok";
    },
    deleteClasses: function () {
      return this.selected ? ["edit-item", "mlt"] : ["edit-item", "pn", "mlt"];
    },
    _containerCss: function () {
      const incomplete =
        this.incompleteMarker && typeof this.incompleteMarker === "object";
      let css = ["cat-repeater-child-summary"];
      if (this.isExpanded) {
        css.push("expanded");
      }
      if (incomplete) {
        css.push("incomplete-marker");
      }

      return this.summaryClasses ? css.concat(this.summaryClasses) : css;
    },
    hasVisibleButtons(): boolean {
      return (
        (this.allowEdit && !this.selected) ||
        (this.allowView && !this.isExpanded) ||
        (this.allowView && this.isExpanded) ||
        (this.allowDelete && !this.selected) ||
        // Check if there's a non-empty buttons slot
        (this.$slots.buttons && this.$slots.buttons().length > 0) ||
        // Check for incomplete marker
        !!this.incompleteMarker
      );
    }
  },
  methods: {
    handleDelete: function (event) {
      this.$emit("ui-repeater-delete", {
        event: event,
        id: this.id ? this.id : null
      });
    },
    handleEdit: function (event) {
      this._selected = true;
      this.$emit("ui-repeater-edit", {
        event: event,
        id: this.id ? this.id : null
      });
    },
    handleView: function (event) {
      this._selected = true;
      this.$emit("ui-repeater-view", {
        event: event,
        id: this.id ? this.id : null
      });
    },
    handleDone: function (event) {
      this._selected = false;
      this.$emit("ui-repeater-done", {
        event: event,
        id: this.id ? this.id : null
      });
    },
    handleToggleDetails: function (event) {
      this.$emit("ui-repeater-toggle-details", {
        event: event,
        id: this.id ? this.id : null
      });
    }
  }
});
</script>
